import { useEffect, useMemo } from 'react';
import '../../assets/css/case-property.css';
// @ts-ignore
import { useContentToken, EditableToken, useCollectionBinding, EditableCollectionBinding } from '@vibesites/runtime/react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import type { RawSpecialOffer } from './propertyUtils';
import { normalizeSpecialOffers } from './propertyUtils';
import { openSpecialOfferPopup } from './SpecialOffers';
import { useAutoRotatingIndex } from './useAutoRotatingIndex';

export function PropertyPromoBanner() {
  // Live special offers via collection binding so edits/new offers show up
  // without a redeploy.
  const rawOffers = useCollectionBinding<RawSpecialOffer>('propertypromobanner.items', {
    collection: 'special_offers',
  });
  const offers = useMemo(() => normalizeSpecialOffers(rawOffers), [rawOffers]);
  const offerTitleOverride = useContentToken('propertypromobanner.offer_title') || '';
  const { currentIndex, setCurrentIndex, hasMultipleItems } = useAutoRotatingIndex(offers.length);
  const offerSignature = useMemo(() => (
    offers
      .map(offer => `${offer.title}::${offer.description}::${offer.disclaimer}`)
      .join('||')
  ), [offers]);
  const showEditableTitle = offers.length <= 1;

  useEffect(() => {
    setCurrentIndex(0);
  }, [offerSignature, setCurrentIndex]);

  if (offers.length === 0) return null;

  return (
    <div className="prop-promo-banner">
      <div className="prop-promo-inner">
        <EditableCollectionBinding
          bindingPath="propertypromobanner.items"
          label="Special Offers"
          className="prop-offer-carousel prop-offer-carousel--banner"
        >
          <div className="prop-offer-carousel-viewport">
            <div
              className="prop-offer-carousel-track"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {offers.map((offer, index) => {
                const offerTitle = showEditableTitle ? offerTitleOverride || offer.title || '' : offer.title || '';
                const offerDescription = offer.description || '';
                const primaryText = offerTitle || offerDescription || 'Special Offer';

                return (
                  <button
                    key={`${offer.title}-${offer.description}-${offer.disclaimer}-${index}`}
                    type="button"
                    className="prop-promo-item prop-offer-carousel-slide"
                    onClick={() => openSpecialOfferPopup(index)}
                    aria-label={`Open special offer ${index + 1}`}
                  >
                    <span className="prop-promo-icon">
                      <svg viewBox="0 0 640 512" width="1em" height="1em" fill="currentColor">
                        <path d="M0 48C0 21.5 21.5 0 48 0H592c26.5 0 48 21.5 48 48V464c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V48zM352 352c0 10.1 6 19.2 15.3 23.1s20.1 1.5 27.5-6L448 315.7l53.3 53.3c7.3 7.5 18.2 9.9 27.5 6S544 362 544 352V64H352V352z"/>
                      </svg>
                    </span>
                    {showEditableTitle ? (
                      <EditableToken path="propertypromobanner.offer_title" type="text" label="Offer Title" as="span" className="prop-promo-label" defaultField={contentSchema.tokens['propertypromobanner.offer_title'].defaultField}>
                        {primaryText}
                      </EditableToken>
                    ) : (
                      <span className="prop-promo-label">{primaryText}</span>
                    )}
                    {offerTitle && offerDescription && (
                      <span className="prop-promo-copy">{offerDescription}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          {hasMultipleItems && (
            <div className="prop-offer-carousel-pagination prop-offer-carousel-pagination--banner">
              {offers.map((offer, index) => (
                <button
                  key={`${offer.title}-${index}`}
                  type="button"
                  className={`prop-offer-carousel-dot${index === currentIndex ? ' is-active' : ''}`}
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`Show offer ${index + 1}`}
                  aria-current={index === currentIndex ? 'true' : undefined}
                />
              ))}
            </div>
          )}
        </EditableCollectionBinding>
      </div>
    </div>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyPromoBanner',
  displayName: 'Property Promo Banner',
  tokens: {
    'propertypromobanner.offer_title': {
      label: 'Offer Title',
      type: 'text',
      defaultField: 'property_specialoffer_title',
      description: 'Special offer title text displayed in the banner',
    },
  },
  collectionBindings: {
    'propertypromobanner.items': {
      label: 'Special Offers',
      collection: 'special_offers',
    },
  },
};

(PropertyPromoBanner as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyPromoBanner;
